import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { isRecommendedMedicationsEmpty } from "@/modules/patients/detail/progress-note/lib/is-recommended-medications-empty"
import { ProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { getDocumentsByCountryFragment } from "@/modules/patients/detail/store/useDocumentStore"
import { useConcludeWithoutDialog } from "@/modules/patients/hooks/useConcludeWithoutDialog"
import { useEditableProgressNotesInContextOfAppointment } from "@/modules/patients/hooks/useEditableProgressNotesInContextOfAppointment"
import { useHandleConclude } from "@/modules/patients/hooks/useHandleConclude"
import { useMissingConsultationReportCheck } from "@/modules/patients/hooks/useMissingConsultationReportCheck"
import { getNumberOfDocumentsToSend } from "@/modules/patients/libs/documents-to-send"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { useDrawerStore } from "@/stores/useDrawerStore"

interface ConcludeActionProps {
  patient: PatientFragment$data
  appointmentId: string | null
  onOpenDialog: (isOpen: boolean) => void
  onCloseMedicalRecord?: () => void
}

export const ConcludeAction = (props: ConcludeActionProps) => {
  const handleConclude = useHandleConclude()
  return <ConcludeActionView {...props} handleConclude={handleConclude} />
}

type ConcludeActionViewProps = ConcludeActionProps & {
  handleConclude: ReturnType<typeof useHandleConclude>
}

const ConcludeActionView = ({
  patient,
  appointmentId,
  onOpenDialog,
  onCloseMedicalRecord,
  handleConclude,
}: ConcludeActionViewProps) => {
  const { t } = useTranslation()
  const closeDrawerWithConfirmation = useDrawerStore((state) => state.closeDrawerWithConfirmation)
  const editableProgressNotes = useEditableProgressNotesInContextOfAppointment()
  const checkMissingConsultationReport = useMissingConsultationReportCheck(editableProgressNotes)
  const [tryConcludeWithoutDialog, isConclusionInFlight] = useConcludeWithoutDialog(patient.age?.years)

  const getConcludeAction = (): {
    action: "CLOSE" | "CONCLUDE" | "CONCLUDE_AND_SEND_DOCUMENTS"
    numberOfDocumentsToSend?: number
  } => {
    const canConcludeAndSendDocuments = editableProgressNotes.some(
      (progressNote) => !isProgressNoteEmpty(progressNote) && !isProgressNotePointOfCareIncomplete(progressNote),
    )

    if (!appointmentId || !canConcludeAndSendDocuments) {
      return { action: "CLOSE" }
    }

    const noPatientContactInfo = !patient.emailAddress || !patient.phoneNumber
    const { numberOfDocumentsToSend, numberOfDocumentsToSendToPatient } =
      getNumberOfDocumentsToSend(editableProgressNotes)
    const hasDocumentsToSend = numberOfDocumentsToSend > 0
    const hasDocumentsToSendToPatient = numberOfDocumentsToSendToPatient > 0

    if (canConcludeAndSendDocuments) {
      if (noPatientContactInfo) {
        if (hasDocumentsToSendToPatient) {
          return {
            action: "CONCLUDE_AND_SEND_DOCUMENTS",
            numberOfDocumentsToSend,
          }
        } else {
          return { action: "CONCLUDE" }
        }
      } else {
        if (hasDocumentsToSend) {
          return {
            action: "CONCLUDE_AND_SEND_DOCUMENTS",
            numberOfDocumentsToSend,
          }
        } else {
          return { action: "CONCLUDE" }
        }
      }
    }

    return { action: "CONCLUDE" }
  }

  const concludeAction = getConcludeAction()

  switch (concludeAction.action) {
    case "CLOSE":
      return (
        <Button
          onClick={() => {
            FaroInsightsWrapper.pushEvent("close_medical_record")
            closeDrawerWithConfirmation({ onConfirm: onCloseMedicalRecord })
          }}
          data-testid="close-consultation-btn">
          {t("common.button.close")}
        </Button>
      )
    case "CONCLUDE":
      return (
        <Button
          variant="primary"
          onClick={() => {
            FaroInsightsWrapper.pushEvent("check_out_conclude")
            closeDrawerWithConfirmation({
              onConfirm: () =>
                handleConclude?.({
                  onValid: () =>
                    checkMissingConsultationReport(async () => {
                      const hasConcluded = await tryConcludeWithoutDialog()

                      if (!hasConcluded) {
                        onOpenDialog(true)
                      }
                    }),
                }),
            })
          }}
          disabled={isConclusionInFlight}
          data-testid="conclude-consultation-btn">
          {t("patient.header.conclude")}
        </Button>
      )
    case "CONCLUDE_AND_SEND_DOCUMENTS": {
      return (
        <Button
          variant="primary"
          onClick={() => {
            FaroInsightsWrapper.pushEvent("check_out_conclude_send")
            closeDrawerWithConfirmation({
              onConfirm: () =>
                handleConclude?.({
                  onValid: () => checkMissingConsultationReport(() => onOpenDialog(true)),
                }),
            })
          }}
          data-testid="conclude-consultation-btn">
          <span className="@xl:hidden">{t("patient.header.conclude")}</span>
          <span className="hidden @xl:block">
            {t("patient.header.concludeAndSendDocuments", { count: concludeAction.numberOfDocumentsToSend })}
          </span>
        </Button>
      )
    }
  }
}

const isProgressNotePointOfCareIncomplete = (data: ProgressNoteData) =>
  data.pointOfCare?.medical === "INCOMPLETE_TELECONSULTATION" ||
  data.pointOfCare?.actual === "INCOMPLETE_TELECONSULTATION"

const isProgressNoteEmpty = (data: ProgressNoteData) =>
  !data.anamnesis &&
  !data.treatmentPlan &&
  !data.pointOfCare &&
  isRecommendedMedicationsEmpty(data) &&
  getDocumentsByCountryFragment(data).length === 0
